import {createSelector} from '@reduxjs/toolkit';

import {EXCLUDED_FILTERS, toAPIFilter} from '@explorer/util/filters';
import {ITEM_TYPES} from '@explorer/constants/item-types';
import {LABS_MAP} from '@explorer/constants';
import {SR_ASSETS} from '@explorer/constants/asset-types';
import {apiSlice} from '@explorer/api/apiSlice';
import {generatePSSceneSearchFilter} from '@explorer/util/items';
import {
  getSelectedImageryCapabilities,
  getSr,
} from '@explorer/selectors/search-filters';
import {getSelectedItemTypes} from './item-types.js';
import {getUpdatedFilters} from './filters.js';
import {sortDateRanges} from '@explorer/util/sort-date-ranges';

export const getDateRanges = state => state.search.dateRanges;

export const getSearchGeometryGeoJson = state => state.search.geometry;
export const getGeometryTooLarge = state => state.search.geometryTooLarge;
export const getGeometryZoomTo = state => state.search.geometryZoomTo;

const selectSavedSearchesResult = apiSlice.endpoints.getSavedSearches.select();
export const getSearches = createSelector(
  selectSavedSearchesResult,
  searchesResults => {
    return searchesResults?.data ?? [];
  }
);

// Get the loaded saved search id (null if not loaded)
export const getSearchId = state => state.search.id;

// ToDo: Stop depending on this lookup. Store loaded saved search instead
export const getLoadedSavedSearch = createSelector(
  getSearchId,
  getSearches,
  (searchId, searches) => searches.find(search => search.id === searchId)
);

export const getSearchFilters = state => state.searchFilters;

const getSingleItemSearchId = state => state.search.singleItemSearchId;

export const filteredByDownloadAccess = state =>
  state && state.labs && state.labs[LABS_MAP.DOWNLOAD_FILTER];

// get array of item type id
export const getSearchItemTypeIds = createSelector(
  [getSelectedItemTypes],
  selectedItemTypes => Object.keys(selectedItemTypes)
);
export const getNoEndDate = state => state.search.noEndDate;

const getSearchFiltersObjFactory = createSelector(
  [
    getSelectedItemTypes,
    getSearchGeometryGeoJson,
    getGeometryTooLarge,
    getDateRanges,
    getSingleItemSearchId,
    getUpdatedFilters,
    getSearchFilters,
    getNoEndDate,
    getSelectedImageryCapabilities,
    getSr,
    (_, useNoEndDate) => useNoEndDate,
  ],
  (
    selectedItemTypes,
    searchGeometry,
    geometryTooLarge,
    dateRanges,
    singleItemSearchId,
    filters,
    searchFilters,
    noEndDate,
    selectedImageryCapabilities,
    sr,
    useNoEndDate
  ) => {
    const searchFiltersObj = [];

    if (singleItemSearchId) {
      /* Handle search for single item id */
      const singleItemFilter = {
        type: 'StringInFilter',
        field_name: 'id',
        config: [singleItemSearchId],
      };

      // Use the item type to allow for updating legacy item types
      // when searching by single item id.
      // This will trigger a change to the filter in worker-manager
      const itemTypeFilter = {
        type: 'StringInFilter',
        field_name: 'item_type',
        config: Object.keys(selectedItemTypes),
      };

      searchFiltersObj.push({
        type: 'AndFilter',
        config: [singleItemFilter, itemTypeFilter],
      });
    } else {
      /* Handle regular search */
      const instruments = Object.keys(searchFilters.instruments).filter(
        key => searchFilters.instruments[key]
      );

      const publishingStages = Object.keys(
        searchFilters.publishingStages
      ).filter(key => searchFilters.publishingStages[key]);

      if (searchGeometry) {
        searchFiltersObj.push({
          type: 'GeometryFilter',
          field_name: 'geometry',
          config: searchGeometry,
        });
      }

      if (!geometryTooLarge) {
        // Note: Running date filters first in query helps data api ES backend
        if (useNoEndDate) {
          if (dateRanges.length) {
            const sortedDateRanges = sortDateRanges(dateRanges);

            searchFiltersObj.push({
              type: 'OrFilter',
              config: sortedDateRanges.map((dates, index) => {
                // Handle No End Date by omitting end date on last

                const dateConfig = {
                  type: 'DateRangeFilter',
                  field_name: 'acquired',
                  config: {
                    gte: dates[0],
                  },
                };

                if (
                  sortedDateRanges.length > 1 &&
                  index !== sortedDateRanges.length - 1
                ) {
                  dateConfig.config.lte = dates[1];
                } else {
                  // If only one date range or last date range and NOT no end date:
                  if (noEndDate === false) {
                    dateConfig.config.lte = dates[1];
                  }
                }

                return dateConfig;
              }),
            });
          }
        } else {
          if (dateRanges.length) {
            searchFiltersObj.push({
              type: 'OrFilter',
              config: dateRanges.map(dates => ({
                type: 'DateRangeFilter',
                field_name: 'acquired',
                config: {
                  gte: dates[0],
                  lte: dates[1],
                },
              })),
            });
          }
        }

        const metadataFilters = filters
          .map(filter => toAPIFilter(filter))
          .filter(Boolean);

        const selectedItemTypesArr = Object.keys(selectedItemTypes);

        searchFiltersObj.push({
          type: 'OrFilter',
          config: selectedItemTypesArr.map(itemType => {
            // PSScene + asset filter handling
            let itemTypeFilter = {};

            if (itemType === ITEM_TYPES.PSSCENE) {
              itemTypeFilter = generatePSSceneSearchFilter(
                selectedImageryCapabilities
              );
            } else {
              itemTypeFilter = {
                type: 'StringInFilter',
                field_name: 'item_type',
                config: [itemType],
              };
            }

            const filteredMetadataFilters = metadataFilters.filter(filter => {
              const filterBlacklist = EXCLUDED_FILTERS[itemType];
              if (
                filterBlacklist &&
                filterBlacklist[filter.field_name] === false
              ) {
                return false;
              }
              return true;
            });

            let instrumentFilter;

            // Item Types that can be used with instrument filter
            const instrumentFilterItemTypes = [
              ITEM_TYPES.PSORTHOTILE,
              ITEM_TYPES.PSSCENE,
            ];
            if (
              instruments.length > 0 &&
              instrumentFilterItemTypes.includes(itemType)
            ) {
              instrumentFilter = {
                type: 'StringInFilter',
                field_name: 'instrument',
                config: instruments,
              };
            }

            let publishStageFilter;

            // Using array with a single item for flexibility. SkySat/PSOrthoTile could also support this filter
            const publishingStageFilterItemTypes = [ITEM_TYPES.PSSCENE];
            if (
              publishingStages.length > 0 &&
              publishingStageFilterItemTypes.includes(itemType)
            ) {
              publishStageFilter = {
                type: 'StringInFilter',
                field_name: 'publishing_stage',
                config: publishingStages,
              };
            }

            return {
              type: 'AndFilter',
              config: [
                itemTypeFilter,
                ...filteredMetadataFilters,
                instrumentFilter,
                publishStageFilter,
              ].filter(item => !!item),
            };
          }),
        });
      }

      // "Asset Capabilities"
      // Surface Reflectance Assets
      if (sr) {
        searchFiltersObj.push({
          type: 'AssetFilter',
          config: SR_ASSETS,
        });
      }

      if (searchFilters.accessPermissions) {
        searchFiltersObj.push({
          type: 'PermissionFilter',
          config: ['assets:download'],
        });
      }

      if (searchFilters.satelliteId) {
        searchFiltersObj.push({
          type: 'StringInFilter',
          field_name: 'satellite_id',
          config: searchFilters.satelliteId
            .split(',')
            .map(satId => satId.trim()),
        });
      }
    }

    return {
      type: 'AndFilter',
      config: searchFiltersObj,
    };
  }
);

export const getSearchFiltersObjForWorker = state =>
  getSearchFiltersObjFactory(state, false);

export const getSearchFiltersObj = state =>
  getSearchFiltersObjFactory(state, true);
